'use client'

import { useEffect, useState } from 'react'
import { HY_TOUR_LINK_CLASS, resolveHyTour } from '@/lib/hyTour'

type HyTourPopupProps = {
  scheduleTourUrl?: string | null
  nonce?: string
}

type HyPopupOptions = {
  id: string
  host: string
  path: string
  triggerType: string
  triggerValue: string
  height: number
  width: number
}

let hyScriptStarted = false

/**
 * Schedule-a-Tour overlay.
 *
 * - Loads Hy.ly `popup.js` (multifamily / client embed)
 * - Also opens our own iframe on `.hytour-link` click (capture), because
 *   HyPopup often fails to bind under React/Next
 *
 * Path comes from CMS TourUrl/CTA URL or site `scheduleTourUrl`.
 */
export default function HyTourPopup({ scheduleTourUrl, nonce }: HyTourPopupProps) {
  const tour = resolveHyTour(scheduleTourUrl)
  const host = tour?.host
  const path = tour?.path
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!host || !path) return

    const options: HyPopupOptions = {
      id: 'toursite',
      host,
      path,
      triggerType: 'click',
      triggerValue: `.${HY_TOUR_LINK_CLASS}`,
      height: 800,
      width: 1250,
    }

    if (!hyScriptStarted) {
      hyScriptStarted = true
      const script = document.createElement('script')
      const protocol =
        window.location.protocol === 'https:' ? 'https://' : 'http://'
      script.src = `${protocol}my.hy.ly/mktg/js/popup.js?v4`
      if (nonce) script.nonce = nonce
      script.onload = () => {
        try {
          const HyPopup = (
            window as Window & {
              HyPopup?: new (options: HyPopupOptions) => unknown
            }
          ).HyPopup
          if (HyPopup) new HyPopup(options)
        } catch {
          /* fall back to iframe overlay below */
        }
      }
      document.body.appendChild(script)
    }

    const onClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (!target.closest(`.${HY_TOUR_LINK_CLASS}`)) return

      // Always open our overlay — reliable under React. HyPopup may also run;
      // our modal is the guaranteed UX.
      event.preventDefault()
      event.stopPropagation()
      setOpen(true)
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [host, path, nonce])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!host || !path) return null

  const iframeSrc = `https://${host}${path}`

  return (
    <>
      <div id="hy-toursite" className="hy-overlay" suppressHydrationWarning />
      {open ? (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-3 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Schedule a Tour"
        >
          <button
            type="button"
            className="absolute inset-0 cursor-pointer"
            aria-label="Close schedule a tour"
            onClick={() => setOpen(false)}
          />
          <div className="relative z-[1] flex h-[min(800px,calc(100dvh-1.5rem))] w-full max-w-[1250px] flex-col overflow-visible bg-white shadow-xl md:h-[min(800px,calc(100dvh-3rem))]">
            {/* Close sits on the top-right corner edge (matches Hy.ly / site popup). */}
            <button
              type="button"
              className="absolute -right-3 -top-3 z-[2] inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black text-white shadow-md hover:bg-black/85 md:-right-4 md:-top-4 md:h-9 md:w-9"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1 1L11 11M11 1L1 11"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <iframe
              title="Schedule a Tour"
              src={iframeSrc}
              className="h-full min-h-0 w-full flex-1 overflow-hidden border-0 bg-white"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        </div>
      ) : null}
    </>
  )
}
